export type Ets1SessionSelectionMode = "checkbox" | "radio_group" | "radio_option";

export type Ets1SessionConfigItem = {
	code: string;
	description: string;
	requiresText?: boolean;
	columnIndex?: number;
	displayOrder?: number;
	parentCode?: string | null;
	selectionMode?: Ets1SessionSelectionMode;
};

export type Ets1SessionTreeItem = {
	code: string;
	description: string;
	requiresText: boolean;
	selectionMode: Ets1SessionSelectionMode;
	children: Ets1SessionTreeItem[];
};

export type Ets1SessionColumnVM = {
	columnIndex: number;
	items: Ets1SessionTreeItem[];
};

function compareSessionDescription(a: string, b: string): number {
	return a.localeCompare(b, undefined, { sensitivity: "base" });
}

function sortSessionTreeAlphabetically(items: Ets1SessionTreeItem[]): Ets1SessionTreeItem[] {
	return [...items]
		.sort((a, b) => compareSessionDescription(a.description, b.description))
		.map((item) => ({
			...item,
			children: sortSessionTreeAlphabetically(item.children),
		}));
}

/** Evenly distribute root items across columns (matches v1 `splitSessionsIntoColumns`). */
function splitRootsIntoColumns(items: Ets1SessionTreeItem[], columnCount: number): Ets1SessionTreeItem[][] {
	if (columnCount <= 0) return [];
	const cols: Ets1SessionTreeItem[][] = Array.from({ length: columnCount }, () => []);
	const perCol = Math.ceil(items.length / columnCount);

	items.forEach((item, idx) => {
		const colIdx = Math.min(Math.floor(idx / perCol), columnCount - 1);
		cols[colIdx].push(item);
	});

	return cols;
}

export type LabeledSessionColumn<T> = {
	key: string;
	title: string;
	showTitle: boolean;
	items: Ets1SessionTreeItem[];
	source: T;
};

/** One TS1-style 4-column grid, with a heading on the first column of each section. */
export function layoutLabeledSessionColumns<T extends { code?: string; title: string; columns: Ets1SessionColumnVM[] }>(
	sections: T[],
	totalColumns = 4,
): LabeledSessionColumn<T>[] {
	const visible = sections
		.map((section) => ({
			section,
			roots: section.columns.flatMap((column) => column.items),
		}))
		.filter((entry) => entry.roots.length);
	if (!visible.length) return [];

	const allocation = allocateSectionColumns(
		visible.map((entry) => entry.roots.length),
		totalColumns,
	);
	const laidOut: LabeledSessionColumn<T>[] = [];

	visible.forEach((entry, index) => {
		const count = allocation[index] ?? 1;
		splitRootsIntoColumns(entry.roots, count).forEach((items, columnIndex) => {
			if (!items.length) return;
			laidOut.push({
				key: `${entry.section.code ?? entry.section.title}-${columnIndex}`,
				title: entry.section.title,
				showTitle: columnIndex === 0,
				items,
				source: entry.section,
			});
		});
	});

	return laidOut;
}

function allocateSectionColumns(itemCounts: number[], totalColumns: number): number[] {
	const allocation: number[] = itemCounts.map((count) => (count > 0 ? 1 : 0));
	let remaining = Math.max(0, totalColumns - allocation.reduce((sum, count) => sum + count, 0));
	const order = itemCounts
		.map((count, index) => ({ count, index }))
		.filter((entry) => entry.count > 0)
		.sort((a, b) => b.count - a.count);

	let cursor = 0;
	let stalled = 0;
	while (remaining > 0 && order.length && stalled < order.length) {
		const target = order[cursor % order.length];
		cursor += 1;
		if (allocation[target.index] >= target.count) {
			stalled += 1;
			continue;
		}
		allocation[target.index] += 1;
		remaining -= 1;
		stalled = 0;
	}

	return allocation;
}

export function buildEts1SessionColumnTree(
	rows: Ets1SessionConfigItem[],
	columnCount = 4,
): Ets1SessionColumnVM[] {
	const byCode = new Map<string, Ets1SessionTreeItem>();
	for (const row of rows) {
		byCode.set(row.code, {
			code: row.code,
			description: row.description,
			requiresText: !!row.requiresText,
			selectionMode: row.selectionMode ?? "checkbox",
			children: [],
		});
	}

	const roots: Ets1SessionTreeItem[] = [];
	for (const row of rows) {
		const item = byCode.get(row.code);
		if (!item) continue;
		const parentCode = row.parentCode?.trim();
		if (parentCode && byCode.has(parentCode)) {
			byCode.get(parentCode)!.children.push(item);
		} else {
			roots.push(item);
		}
	}

	const sortedRoots = sortSessionTreeAlphabetically(roots);
	const columnItems = splitRootsIntoColumns(sortedRoots, columnCount);

	return columnItems.map((items, index) => ({
		columnIndex: index,
		items,
	}));
}

export function flattenEts1SessionTree(columns: Ets1SessionColumnVM[]): Ets1SessionTreeItem[] {
	const result: Ets1SessionTreeItem[] = [];
	const walk = (items: Ets1SessionTreeItem[]) => {
		for (const item of items) {
			result.push(item);
			if (item.children.length) walk(item.children);
		}
	};
	for (const column of columns) walk(column.items);
	return result;
}

export function filterSessionTreeBySearch(
	columns: Ets1SessionColumnVM[],
	query: string,
	columnCount = 4,
): Ets1SessionColumnVM[] {
	const normalized = query.trim().toLowerCase();
	if (!normalized) return columns;

	const filterItems = (items: Ets1SessionTreeItem[]): Ets1SessionTreeItem[] =>
		items
			.map((item) => {
				const children = filterItems(item.children);
				const selfMatch =
					item.description.toLowerCase().includes(normalized) ||
					item.code.toLowerCase().includes(normalized);
				if (!selfMatch && !children.length) return null;
				return { ...item, children };
			})
			.filter((item): item is Ets1SessionTreeItem => !!item);

	const matchedRoots = sortSessionTreeAlphabetically(
		columns.flatMap((column) => filterItems(column.items)),
	);
	const columnItems = splitRootsIntoColumns(matchedRoots, columnCount);

	return columnItems.map((items, index) => ({
		columnIndex: index,
		items,
	}));
}

export function filterSessionsToFormTemplate(
	catalog: Ets1SessionConfigItem[],
	selectedCodes: string[],
): Ets1SessionConfigItem[] {
	const selected = new Set(selectedCodes);
	const selectedParents = new Set(
		catalog
			.filter((row) => selected.has(row.code) && row.parentCode)
			.map((row) => row.parentCode as string),
	);

	return catalog.filter((row) => {
		if (selected.has(row.code)) return true;
		if (row.parentCode && selected.has(row.parentCode)) return true;
		if (selectedParents.has(row.code)) return true;
		return false;
	});
}

export function collectEts1SessionCodes(columns: Ets1SessionColumnVM[]): string[] {
	return flattenEts1SessionTree(columns).map((item) => item.code);
}

/** Radio group with children, or a checkbox whose children are all radio options. */
export function hasEts1RadioChildren(item: Ets1SessionTreeItem): boolean {
	if (!item.children.length) return false;
	if (item.selectionMode === "radio_group") return true;
	return item.selectionMode === "checkbox" && item.children.every((child) => child.selectionMode === "radio_option");
}
